import React, {ReactElement} from "react";

import {css} from "/src/utils/utils";
import {submitFormData} from "/src/utils/forms";

import {SimpleFormProps, MultiFormProps} from "/src/types";

import Steps from "../steps/Steps";
import SimpleFormWrapper from "../simple-form-wrapper/SimpleFormWrapper";
import GDPRContext from "/src/contexts/GDPRContext";

import * as styles from "./multi-form.module.scss";

const MultiForm = (props: MultiFormProps): ReactElement | null => {
  const {
    forms,
    progressBar,
    onSubmitCallback,
    useLegacyPhoneInput = false,
    showGDPR = false,
  } = props;

  const renderProgressBar = progressBar === true;

  const gdpr = React.useContext(GDPRContext);
  const [formData, setFormData] = React.useState<{}>({});
  const [step, setStep] = React.useState<number>(0);

  /**
   * Consolidates labels from all nested forms.
   *
   * @param {Array<SimpleFormProps>} _forms
   * @return {Array<string>}
   */
  const getLabels = (_forms: Array<SimpleFormProps>): Array<string> => {
    return _forms.map(form => (form != null ? form.stepEyebrowLabel! : ""));
  };

  /**
   * Gets value of next step confined within 0 and maximum value.
   *
   * @param {number} value
   * @return {number}
   */
  const getStep = (value: number): number => {
    const max = forms != null ? forms.length - 1 : 0;

    return Math.max(0, Math.min(value + 1, max));
  };

  /**
   * Handler for form submission.
   * Save data returned from <SimpleForm> and either go to the next step or
   * submit all the form data collected.
   *
   * @param {number} index
   * @param {Record<string, any>} data
   */
  const submitHandler = (
    index: number,
    data: Record<string, any>,
    postSubmissionUrl?: string | null | undefined
  ): Promise<boolean> => {
    return new Promise(async (resolve, reject) => {
      if (forms == null) {
        resolve(false);
        return;
      }

      // GROW-2106: we should merge the data with comma separator
      // if there is existing data in the same field name
      // scenario:
      // 1. in step 1 there is a field called multi-select-1, and user selected option-1 value
      // 2. in step 2 there is a field called multi-select-1, and user selected option-3 value
      // 3. the final value of multi-select-1 field in step 2 should be option-1,option-3
      const consolidatedFormData = Object.entries(data).reduce(
        (acc, [key, value]) => {
          if (value != null) {
            const temp = String(value);
            const dataVal = acc[key as keyof typeof acc] as string;

            return {
              ...acc,
              [key]: dataVal
                ? // this is to avoid non multi select data fields get concatenated
                  [
                    ...new Set([
                      ...dataVal.split(","),
                      ...temp.toString().split(","),
                    ]),
                  ].join(",")
                : value,
            };
          }
          return {...acc};
        },
        {...formData}
      );

      let isLastStep = false;
      let currentStep = step;

      // we assume that, if postsubmissionurl is provided, it is a conditional form
      // TODO: refactor this to be more explicit
      const hasConditionalStep = postSubmissionUrl != null;
      const finalSubmitUrl = forms[currentStep]
        ? forms[currentStep].postSubmissionUrl
        : null;

      try {
        await submitFormData(consolidatedFormData, gdpr);

        if (hasConditionalStep) {
          // FMH-367: if postSubmissionUrl is provided, we should redirect to that url
          // let's redirect
          // this is to support conditional form

          // let's add a step to replace any templating value in the url with the form data. e.g. {{email}}
          const finalUrl: string = Object.entries(consolidatedFormData).reduce(
            (acc: string, [key, value]) => {
              return value
                ? acc.replace(new RegExp(`{{${key}}}`, "g"), `${value}`)
                : "";
            },
            postSubmissionUrl
          );

          window.location.assign(finalUrl);
          return;
        }

        if (hasConditionalStep) {
          // FMH-367: if postSubmissionUrl is provided, we should redirect to that url
          // let's redirect
          // this is to support conditional form

          // let's add a step to replace any templating value in the url with the form data. e.g. {{email}}
          const finalUrl: string = Object.entries(consolidatedFormData).reduce(
            (acc: string, [key, value]) => {
              return value
                ? acc.replace(new RegExp(`{{${key}}}`, "g"), `${value}`)
                : "";
            },
            postSubmissionUrl
          );

          window.location.assign(finalUrl);
          return;
        }

        if (index < forms.length - 1) {
          currentStep = getStep(step);
          setStep(currentStep);
        } else {
          isLastStep = true;
        }

        setFormData(consolidatedFormData);

        if (onSubmitCallback) {
          await onSubmitCallback(index, undefined, formData);
        }
        resolve(true);
      } catch (err: any) {
        if (onSubmitCallback) {
          await onSubmitCallback(index, err, formData);
        }
        reject(err);
        return;
      }

      // wait till submit form data task is done, then redirect user
      if (isLastStep && finalSubmitUrl != null) {
        window.location.assign(finalSubmitUrl);
      }
    });
  };

  if (forms == null) {
    return null;
  }

  return (
    <div className={css(styles.multiForm)}>
      {renderProgressBar && (
        <Steps labels={getLabels(forms)} activeStep={step} />
      )}

      {/* eslint-disable no-underscore-dangle */}
      <div className={css(styles.multiForm__el)}>
        <div
          className={css(styles.multiForm__forms)}
          style={{left: `${step * -100}%`}}
        >
          {forms.map(
            (form, index) =>
              form != null && (
                <SimpleFormWrapper
                  key={`form-${form.title != null ? form.title : index}-${
                    form.contentful_id
                  }`}
                  contentful_id={form.contentful_id}
                  __typename={form.__typename}
                  darkMode={form.darkMode}
                  classes={styles.multiForm__form}
                  disableSubmit
                  errorMessage={form.errorMessage}
                  fieldItems={form.fieldItems}
                  footnote={form.footnote}
                  isMultiStep
                  layout={form.layout}
                  name={form.name}
                  onSubmitCallback={(data, err, postSubmissionUrl) =>
                    submitHandler(index, data, postSubmissionUrl)
                  }
                  postSubmissionUrl={form.postSubmissionUrl}
                  stepEyebrowLabel={form.stepEyebrowLabel}
                  submitButtonLabel={form.submitButtonLabel}
                  submitButtonId={form.submitButtonId}
                  title={form.title}
                  formId={form.formId}
                  // multi step form item should not be embedded
                  embedded={false}
                  campaignId={form.campaignId}
                  lastInquiryObject={form.lastInquiryObject}
                  devCampaignId={form.devCampaignId}
                  footnoteWithOrDivider={form.footnoteWithOrDivider}
                  // we only want to show the success message (if any) on last step
                  // this is to force the above behaviour
                  submittedSuccessfullyMessage={
                    index === forms.length - 1
                      ? form.submittedSuccessfullyMessage
                      : null
                  }
                  successMessageId={form.successMessageId}
                  pardotFormHandlerUrl={null}
                  showGDPR={showGDPR}
                  useLegacyPhoneInput={useLegacyPhoneInput}
                />
              )
          )}
          {/* eslint-disable no-underscore-dangle */}
        </div>
      </div>
    </div>
  );
};

export default MultiForm;
